import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { EntityStore, QueryEntity } from "@datorama/akita";
import { environment } from "@env/environment";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { TranslateService } from "@ngx-translate/core";
import {
	ClientsQuery,
	ComegoQuery,
	FeedQuery,
	Logger,
	MyTimesQuery,
	ProjectsQuery,
	TagsQuery,
	TasksQuery,
	Time,
	UserSettings,
	UserSettingsQuery,
	WorkspacesQuery,
} from "@timeghost/web-api";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map, switchMap } from "rxjs/operators";
import { MixedTimesSyncService } from "../mixed-times-sync.service";
import { SignalrChangeMessage, SignalrChangeType, SignalrHubAuthenticationResponse } from "./signalr.types";
import { createSignalrHub } from "./signalr-hub.factory";
import { SignalrLabelResolverUtil } from "./signalr-label-resolver.util";

const log = new Logger("SignalrService");

function getStoreFromQuery<TEntity>(query: QueryEntity<TEntity>): EntityStore<TEntity> {
	return query.__store__ as EntityStore<TEntity>;
}

function cosmosEntityNameFromPartitionKey(partitionKey: unknown): string | undefined {
	if (typeof partitionKey !== "string") return undefined;
	if (partitionKey.endsWith("-comego")) return "comego";
	return undefined;
}

@Injectable()
export class SignalrService {
	private hubConnection: HubConnection | null = null;
	private accessToken = "";
	private readonly netState = new Subject<"online" | "offline">();
	private readonly hubClose = new Subject<unknown>();
	private isInitialized = false;
	private createNewInstancePromise: Promise<void> | null = null;
	private shouldEmitHubClose = true;

	readonly onMessage = new EventEmitter<SignalrChangeMessage>(true);
	readonly onHubClose$ = this.hubClose.asObservable().pipe(distinctUntilChanged());

	constructor(
		private readonly httpClient: HttpClient,
		private readonly feedQuery: FeedQuery,
		private readonly myTimesQuery: MyTimesQuery,
		private readonly userSettingsQuery: UserSettingsQuery,
		private readonly projectsQuery: ProjectsQuery,
		private readonly tagsQuery: TagsQuery,
		private readonly tasksQuery: TasksQuery,
		private readonly workspacesQuery: WorkspacesQuery,
		private readonly clientsQuery: ClientsQuery,
		private readonly comegoQuery: ComegoQuery,
		private readonly translateService: TranslateService,
		private readonly mixedTimesSyncService: MixedTimesSyncService
	) {}

	initialize(): Promise<void> {
		if (this.isInitialized) return Promise.resolve();
		this.isInitialized = true;
		this.bindNetworkState();
		return this.createNewInstance(true).then(() => undefined);
	}

	isInstanceConnected(): boolean {
		return !!(
			this.hubConnection &&
			this.hubConnection.connectionId != null &&
			[HubConnectionState.Connected, HubConnectionState.Connecting, HubConnectionState.Reconnecting].includes(
				this.hubConnection.state
			)
		);
	}

	private stoppingHub = false;
	private async stop() {
		this.shouldEmitHubClose = false;
		this.stoppingHub = true;
		return await this.hubConnection?.stop().finally(() => {
			this.stoppingHub = false;
			this.hubConnection = null;
		});
	}
	private get isConnecting() {
		const state = this.hubConnection?.state;
		return state && (state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting);
	}
	async createNewInstance(createNewHub = false): Promise<void> {
		if (navigator.onLine === false) return;
		if (this.isInstanceConnected() && !createNewHub) return;
		if (this.stoppingHub || this.isConnecting) return;
		if (this.createNewInstancePromise) return this.createNewInstancePromise;
		this.createNewInstancePromise = (async () => {
			this.shouldEmitHubClose = false;
			const { url, accessToken } = await this.fetchHubAuthentication();
			this.accessToken = accessToken;
			if (this.hubConnection?.connectionId) await this.stop();
			if (!this.hubConnection || createNewHub) this.hubConnection = this.createHub(url);
			this.bindHubCallbacks(this.hubConnection);
			try {
				await this.hubConnection.start();
				this.shouldEmitHubClose = true;
				log.debug("Real-time connection started");
			} catch (error) {
				log.error("Error while establishing signalr connection", error);
				throw error;
			}
		})().finally(() => {
			this.createNewInstancePromise = null;
		});
		return this.createNewInstancePromise;
	}

	private bindNetworkState(): void {
		if ("ononline" in window && "onoffline" in window) {
			fromEvent(window, "online").subscribe(() => this.netState.next("online"));
			fromEvent(window, "offline").subscribe(() => this.netState.next("offline"));
		}
		this.netState
			.asObservable()
			.pipe(
				map((eventType) => eventType === "online"),
				distinctUntilChanged(),
				debounceTime(1000),
				switchMap(async (isOnline) => {
					if (isOnline && !this.isInstanceConnected()) {
						await this.createNewInstance(!this.hubConnection);
					}
					if (!isOnline && this.hubConnection) {
						await this.stop();
					}
				})
			)
			.subscribe();
	}

	private createHub(url: string): HubConnection {
		return createSignalrHub({
			url,
			accessTokenFactory: async () => this.accessToken,
		});
	}

	private bindHubCallbacks(hub: HubConnection): void {
		hub.off("changes");
		hub.on("changes", (message: SignalrChangeMessage) => this.handleChangeMessage(message));
		hub.onclose((error) => {
			log.info("Live synchronization closed");
			if (this.stoppingHub || this.isConnecting || this.createNewInstancePromise) return;
			if (!this.shouldEmitHubClose) return;
			if (navigator.onLine === false) return;
			this.hubClose.next(error ?? Date.now());
		});
	}

	private fetchHubAuthentication(): Promise<SignalrHubAuthenticationResponse> {
		return this.httpClient
			.post<SignalrHubAuthenticationResponse>(environment.serverUrl + "/start_realtime", null)
			.toPromise();
	}
	private subscribeToWorkspaceGroup() {
		return this.httpClient.post<void>(environment.serverUrl + "/start_realtime_group", null).toPromise();
	}

	private handleChangeMessage(message: SignalrChangeMessage): void {
		const payload = message?.payload as Record<string, unknown>;
		if (!payload) return;
		if (payload.cosmosEntityName === "times") {
			this.handleTimeMessage(message);
			this.onMessage.emit(message);
			return;
		}
		if (payload.cosmosEntityName === "users") {
			const newUserSettings = payload as unknown as UserSettings;
			if (newUserSettings.id === this.userSettingsQuery.getValue().id)
				this.userSettingsQuery.__store__.update(newUserSettings);
			this.onMessage.emit(message);
			return;
		}
		this.handleGenericTypeMessage(message.type, payload);
		this.onMessage.emit(message);
	}

	private handleTimeMessage(message: SignalrChangeMessage): void {
		const time = message.payload as Time;
		if (message.type === SignalrChangeType.Added) this.upsertTime(time, "add");
		if (message.type === SignalrChangeType.Updated) this.upsertTime(time, "update");
		if (message.type === SignalrChangeType.Deleted) this.deleteTime(time);
	}

	private upsertTime(time: Time, mode: "add" | "update"): void {
		const userSettings = this.userSettingsQuery.getValue();
		const currentWorkspaceId = userSettings?.workspace?.id;
		if (!currentWorkspaceId) return;
		if (time.user.id !== userSettings.id || time.workspace.id !== currentWorkspaceId) return;
		if (time.outlookCalenderReference)
			this.feedQuery.__store__.upsert(time.outlookCalenderReference, { booked: true });
		if (mode === "add") this.myTimesQuery.__store__.add(time);
		else this.myTimesQuery.__store__.update(time.id, time);
	}

	private deleteTime(time: Time): void {
		const userSettings = this.userSettingsQuery.getValue();
		const currentWorkspaceId = userSettings?.workspace?.id;
		if (!currentWorkspaceId) return;
		const existingTime = this.myTimesQuery.getEntity(time.id);
		if (!existingTime) return;
		void this.mixedTimesSyncService.handleProjectTimeDelete(existingTime);
		if (
			existingTime.user.id === userSettings.id &&
			existingTime.workspace.id === currentWorkspaceId &&
			existingTime.outlookCalenderReference
		) {
			this.feedQuery.__store__.upsert(existingTime.outlookCalenderReference, { booked: false });
		}
		this.myTimesQuery.__store__.remove(time.id);
	}

	private handleGenericTypeMessage(type: SignalrChangeType, rawPayload: Record<string, unknown>): boolean {
		const cosmosEntityName =
			rawPayload.cosmosEntityName ?? cosmosEntityNameFromPartitionKey(rawPayload.partitionKey);
		if (!cosmosEntityName) return false;
		const userSettings = this.userSettingsQuery.getValue();
		const payload = this.filterWithoutData(rawPayload) as Record<string, unknown>;
		const workspaceId = (payload?.workspace as { id?: string })?.id;
		if (cosmosEntityName !== "workspaces" && workspaceId && workspaceId !== userSettings.workspace.id) return false;

		const store = ({
			projects: getStoreFromQuery(this.projectsQuery),
			tags: getStoreFromQuery(this.tagsQuery),
			tasks: getStoreFromQuery(this.tasksQuery),
			workspaces: getStoreFromQuery(this.workspacesQuery),
			clients: getStoreFromQuery(this.clientsQuery),
			comego: getStoreFromQuery(this.comegoQuery),
		}[String(cosmosEntityName)] ?? null) as EntityStore<any> | null;

		if (!store) return false;
		if (type === SignalrChangeType.Added) store.add(payload as any);
		if (type === SignalrChangeType.Updated) {
			store.upsert(payload.id as string, payload as any);
			if (cosmosEntityName === "workspaces") {
				const currentWorkspaceId = userSettings?.workspace?.id;
				if (currentWorkspaceId && payload.id === currentWorkspaceId)
					this.userSettingsQuery.__store__.update({ workspace: payload as any });
			}
		}
		if (type === SignalrChangeType.Deleted) store.remove(payload.id as string);
		return true;
	}

	private filterWithoutData(payload: Record<string, unknown>): Record<string, unknown> {
		const withoutClientLabel = SignalrLabelResolverUtil.resolveClientLabel(this.translateService);
		const withoutProjectLabel = SignalrLabelResolverUtil.resolveProjectLabel(this.translateService);
		const entityName = String(payload.entityName ?? "");
		const client = payload.client as { name?: string };
		const project = payload.project as { name?: string };

		if (SignalrLabelResolverUtil.isWithoutClientLabel(client?.name)) client.name = withoutClientLabel;
		if (SignalrLabelResolverUtil.isWithoutProjectLabel(project?.name)) project.name = withoutProjectLabel;
		if (entityName === "projects" && SignalrLabelResolverUtil.isWithoutProjectLabel(String(payload.name ?? "")))
			payload.name = withoutProjectLabel;
		return payload;
	}
}
